import { LogOut, Monitor, Smartphone } from "lucide-mui";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import toast from "react-hot-toast";
import { api } from "../../../api/client.ts";
import { keys } from "../../../api/queryKeys.ts";
import ConfirmDialog from "../../../components/ConfirmDialog.tsx";
import FormattedDate from "../../../components/FormattedDate.tsx";
import IdentityCell from "../../../components/IdentityCell.tsx";
import RelativeTime from "../../../components/RelativeTime.tsx";
import RowActions from "../../../components/RowActions.tsx";
import SectionHeading from "../../../components/SectionHeading.tsx";
import EmptyTableRow from "../../../components/table/EmptyTableRow.tsx";
import ScrollTable from "../../../components/table/ScrollTable.tsx";
import TableCard from "../../../components/table/TableCard.tsx";
import { describeUserAgent } from "../../../utils/userAgent.ts";

/** The browsers you're signed in on, and signing them out. */
export default function SessionsSection() {
  const queryClient = useQueryClient();
  const [confirmOthers, setConfirmOthers] = useState(false);

  const sessions = useQuery({ queryKey: keys.account.sessions(), queryFn: api.getSessions });
  const refresh = () => queryClient.invalidateQueries({ queryKey: keys.account.sessions() });

  const revoke = useMutation({
    mutationFn: api.revokeSession,
    onSuccess: () => {
      refresh();
      toast.success("Session signed out");
    },
  });
  const revokeOthers = useMutation({
    mutationFn: api.revokeOtherSessions,
    onSuccess: ({ revoked }) => {
      refresh();
      setConfirmOthers(false);
      toast.success(revoked === 1 ? "1 session signed out" : `${revoked} sessions signed out`);
    },
  });

  const rows = sessions.data ?? [];
  const others = rows.filter((s) => !s.current).length;

  return (
    <Box>
      <SectionHeading
        title="Active sessions"
        count={sessions.data ? rows.length : undefined}
        action={
          <Button
            size="small"
            variant="outlined"
            startIcon={<LogOut />}
            disabled={others === 0}
            onClick={() => setConfirmOthers(true)}
          >
            Sign out other sessions
          </Button>
        }
      />
      <TableCard loading={sessions.isLoading}>
        <ScrollTable>
          <TableHead>
            <TableRow>
              <TableCell>Browser</TableCell>
              <TableCell sx={{ whiteSpace: "nowrap" }}>Signed in</TableCell>
              <TableCell sx={{ whiteSpace: "nowrap" }}>Last active</TableCell>
              <TableCell padding="checkbox" />
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((s) => {
              const ua = describeUserAgent(s.user_agent);
              return (
                <TableRow key={s.id}>
                  <TableCell>
                    <IdentityCell
                      avatar={
                        <Box sx={{ display: "flex", color: "text.secondary", "& svg": { fontSize: 20 } }}>
                          {ua.device === "mobile" ? <Smartphone /> : <Monitor />}
                        </Box>
                      }
                      name={ua.label}
                      tag={s.current ? "This browser" : undefined}
                      caption={s.ip_address ?? undefined}
                    />
                  </TableCell>
                  <TableCell sx={{ whiteSpace: "nowrap" }}>
                    <FormattedDate value={s.created_at} />
                  </TableCell>
                  <TableCell sx={{ whiteSpace: "nowrap" }}>
                    {s.current ? "Now" : <RelativeTime value={s.last_active_at} />}
                  </TableCell>
                  <TableCell padding="checkbox" align="right">
                    <RowActions
                      label={ua.label}
                      actions={[
                        {
                          label: "Sign out",
                          icon: <LogOut />,
                          destructive: true,
                          hidden: s.current,
                          onClick: () => revoke.mutateAsync(s.id),
                          confirm: {
                            title: `Sign out: ${ua.label}`,
                            body: "That browser has to sign in again.",
                            confirmLabel: "Sign out",
                          },
                        },
                      ]}
                    />
                  </TableCell>
                </TableRow>
              );
            })}
            {rows.length === 0 && <EmptyTableRow colSpan={4}>No active sessions</EmptyTableRow>}
          </TableBody>
        </ScrollTable>
      </TableCard>
      <ConfirmDialog
        open={confirmOthers}
        title="Sign out other sessions"
        confirmLabel="Sign out"
        pending={revokeOthers.isPending}
        onConfirm={() => revokeOthers.mutate()}
        onClose={() => setConfirmOthers(false)}
      >
        Every browser but this one has to sign in again.
      </ConfirmDialog>
    </Box>
  );
}
